import { queryOptions } from '@tanstack/react-query';
import { env } from '@/lib/env';
import { getDevAuthToken } from '@/lib/api/dev-auth';
// The session key alone, not the whole key factory (query-keys.ts), which signed-in pages load with them.
import { sessionKey } from './session-key';

export interface SessionUser {
  id: string;
  email: string;
  name: string | null;
  image: string | null;
}

/** The API couldn't answer (down, restarting, a deploy): not the same as signed out. */
export class SessionUnavailableError extends Error {
  constructor(reason: string) {
    super(`Couldn't check your session: ${reason}`);
    this.name = 'SessionUnavailableError';
  }
}

/**
 * The signed-in person, or null — better-auth's `GET /api/auth/get-session`
 * read with a plain fetch. Every route guard runs this, so it stays out of
 * the better-auth client (which, with its zod dependency, would otherwise sit
 * in the entry chunk the sign-in page pays for). Signing in and out still go
 * through the better-auth client (`lib/auth-client`), loaded when needed.
 *
 * A dev API-key login (`?e2eAuthToken=`) counts as signed in, as in web-v2.
 * An API that can't answer throws SessionUnavailableError instead of saying
 * "signed out", so a restart or deploy never sends someone to sign in.
 */
export async function fetchSessionUser(): Promise<SessionUser | null> {
  if (getDevAuthToken()) {
    return { id: 'dev-api-key', email: 'dev-api-key@system.dreamwell.ai', name: 'Local dev login', image: null };
  }
  let response: Response;
  try {
    // eslint-disable-next-line no-restricted-syntax -- the one sanctioned raw fetch: see the comment above (keeps zod out of the entry chunk)
    response = await fetch(`${env.backendUrl}/api/auth/get-session`, { credentials: 'include', headers: { accept: 'application/json' } });
  } catch (e) {
    throw new SessionUnavailableError(e instanceof Error ? e.message : 'unreachable');
  }
  if (response.status >= 500) throw new SessionUnavailableError(`HTTP ${response.status}`);
  if (!response.ok) return null;
  const body = (await response.json().catch(() => null)) as { user?: { id?: unknown; email?: unknown; name?: unknown; image?: unknown } } | null;
  const u = body?.user;
  if (!u || typeof u.id !== 'string' || typeof u.email !== 'string') return null;
  return {
    id: u.id,
    email: u.email,
    name: typeof u.name === 'string' && u.name ? u.name : null,
    image: typeof u.image === 'string' ? u.image : null,
  };
}

export const sessionQuery = queryOptions({
  queryKey: sessionKey(),
  queryFn: fetchSessionUser,
  staleTime: 5 * 60_000,
  // Long enough to ride out an API restart (1s, 2s, 4s, 8s); a real answer is never retried.
  retry: (failures, error) => error instanceof SessionUnavailableError && failures < 4,
});
